import React, {useCallback, useEffect, useRef, useState} from 'react'

import L from './leaflet.js'
import OrderActions from '../../actions/OrderActions.js'
import t from '../../helpers/Languages.js'
import OrderStore from '../../stores/OrderStore.js'
import {getAvailableBranches} from '../../utils/branches.js'
import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import {User} from '../../../server/functions/users/users.types.js'
import socket from '../../socketio/socketio.js'
import escapeHtml from '../../../utils/escapeHtml.js'
import {Button, Colors, Popover, Row} from '../UI/index.js'
import {FormStack, Toggle} from '../UI/forms/index.js'
import {addScaleControl, baseMapOptions, createPane, createTileLayer, defaultCenter, defaultZoom, fitBoundsSafe, panes} from './setup.js'
import {
    addAreaSelection,
    buildPlanningMapModel,
    getMarkerBorderColor,
    getOrderPositions,
    getPlanningDepots,
    isMarkerSelected,
    toggleMarkerSelection,
    LatLng,
    MarkerModel,
    PlanningMapModel,
    PlanningOrder,
    PlanningRoute,
    PolylineModel
} from './planningMapModel.js'
import useLeafletMap from './hooks/useLeafletMap.js'
import useLayerGroup from './hooks/useLayerGroup.js'
import useMapSelection, {SelectionShape} from './hooks/useMapSelection.js'
import {isMacPlatform, lassoKeyLabel} from './selectionKeys.js'
import useLatest from './hooks/useLatest.js'
import MapControls from './controls/MapControls.js'
import MapControlCard from './controls/MapControlCard.js'
import MapControlButton from './controls/MapControlButton.js'
import MapAttribution from './controls/MapAttribution.js'
import MapLoadingOverlay from './controls/MapLoadingOverlay.js'
import {mapContainerStyle, mapElementStyle, mapSpacing, mapZIndex} from './styles.js'

export interface PlanningMapOptions {
    showDepotsOnMap?: boolean
    showExpectedOnMap?: boolean
}

// The key that selects with the lasso while held: Ctrl, on a Mac Cmd (Ctrl works there as well)
const lassoKey = lassoKeyLabel(isMacPlatform())

export interface PlanningMapProps {
    reseller: Reseller
    user: User
    selectedBranchIds?: string[]
    loading?: boolean
    date: string
    orders: Record<string, PlanningOrder>
    routes: Record<string, PlanningRoute>
    selectedOrders: string[]
    options: PlanningMapOptions
    showOrdersOnMap?: boolean
    showCompletedAddressesOnMap?: boolean
    showRoutesOnMap?: boolean
    showStopNumbersOnMap?: boolean
    focusedRouteId?: string
    someRouteHidden?: boolean
    activeRouteTab: string
    /** Centre on the first selected order (briefly true after switching to the map from an order) */
    centerViewOnSelected?: boolean
    /** A marker with orders is clicked (not while selecting) */
    onClick: (orderIds: string[]) => void
    /** The selection changed on the map */
    onChange: (orderIds: string[]) => void
}

interface MapView {
    center?: LatLng
    zoom?: number
    autoFitPending?: boolean
}

interface MarkerEntry {
    model: MarkerModel
    layer: L.CircleMarker | L.Marker
    group: L.LayerGroup
    selected: boolean
}

interface PolylineEntry {
    model: PolylineModel
    layer: L.Polyline
}

interface UpdateKinds {
    /** The data changed: rebuild the model and the layers that changed */
    data?: boolean
    /** Only the selection changed: restyle the markers */
    selection?: boolean
    /** Centre on the selected order */
    center?: boolean
}

// The planner's map view, kept in the store (in memory) so it survives a remount of the map
const getStoredMapView = () => (OrderStore as unknown as {singleton?: {mapView?: MapView}}).singleton?.mapView

/**
 * The map of the planner: the stops of the unplanned orders and the routes, the routes' lines, depots, start and
 * finish. Clicking a marker opens its orders, selecting (a box with the toolbar or Shift, a free-hand lasso with the
 * toolbar or Ctrl) adds the orders in the drawn area to the selection. The layers are kept by key and only rebuilt
 * when they change; a change of the selection only restyles the markers. Updates are applied once per frame.
 */
export default function PlanningMap(props: PlanningMapProps) {
    const {
        reseller, user, selectedBranchIds, loading, date, orders, routes, selectedOrders, options, showOrdersOnMap,
        showCompletedAddressesOnMap, showRoutesOnMap, showStopNumbersOnMap, focusedRouteId, someRouteHidden,
        activeRouteTab, centerViewOnSelected
    } = props

    const containerRef = useRef<HTMLDivElement>(null)
    const latest = useLatest(props)
    // The selection switched on with the toolbar
    const [selectionMode, setSelectionMode] = useState<SelectionShape | null>(null)
    const [optionsOpen, setOptionsOpen] = useState(false)
    const selectingRef = useRef(false)
    const lineRenderer = useRef<L.Canvas | null>(null)

    const autoFitPending = useRef(getStoredMapView()?.autoFitPending ?? true)

    // The map zooms to the depots + visible stops once per session, unless the user moved the map before the stops
    // were loaded
    const stopAutoFit = useCallback(() => {
        if (autoFitPending.current) {
            autoFitPending.current = false
            OrderActions.setMapView({autoFitPending: false})
        }
    }, [])

    const map = useLeafletMap(containerRef, {...baseMapOptions, doubleClickZoom: false}, (map) => {
        const {reseller, user, selectedBranchIds} = latest.current
        const mapView = getStoredMapView()
        const {fitPositions} = getDepots(reseller, user, selectedBranchIds)

        if (mapView?.center) {
            map.setView(mapView.center, mapView.zoom)
        } else if (fitPositions.length > 1) {
            fitBoundsSafe(map, fitPositions, {padding: [50, 50]})
        } else {
            map.setView(fitPositions[0] || defaultCenter, defaultZoom)
        }

        createTileLayer().addTo(map)
        addScaleControl(map)
        createPane(map, panes.lowMarkers)
        createPane(map, panes.routeLines)
        // The lines get their own canvas below the markers' canvas, so a line that is redrawn never covers the markers
        lineRenderer.current = L.canvas({pane: panes.routeLines.name})

        map.on('moveend', () => {
            const {lat, lng} = map.getCenter()
            OrderActions.setMapView({center: {lat, lng}, zoom: map.getZoom()})
        })

        map.on('dragstart', stopAutoFit)
        // The selection keys don't move the map
        map.on('keydown', (event: L.LeafletKeyboardEvent) => !['Shift', 'Control', 'Meta'].includes(event.originalEvent.key) && stopAutoFit())

        const container = map.getContainer()
        container.addEventListener('wheel', stopAutoFit)
        container.addEventListener('touchstart', stopAutoFit)

        map.on('click', () => {
            const {onChange, selectedOrders} = latest.current

            if (!selectingRef.current && selectedOrders.length) {
                onChange([])
            }
        })

        return () => {
            container.removeEventListener('wheel', stopAutoFit)
            container.removeEventListener('touchstart', stopAutoFit)
        }
    })

    const selecting = useMapSelection(map, {
        mode: selectionMode,
        onSelect: (polygon) => {
            const {onChange, selectedOrders} = latest.current
            const markers = modelRef.current?.markers || []
            const selection = addAreaSelection(selectedOrders, markers, polygon)

            if (selection.length !== selectedOrders.length) {
                onChange(selection)
            }
        }
    })
    selectingRef.current = !!selecting

    const depotGroup = useLayerGroup(map)
    const markerGroup = useLayerGroup(map)
    const lineGroup = useLayerGroup(map)
    const modelRef = useRef<PlanningMapModel | null>(null)
    const markerLayers = useRef(new Map<string, MarkerEntry>())
    const lineLayers = useRef(new Map<string, PolylineEntry>())
    const lastFocusedRouteId = useRef<string | undefined>(undefined)
    const vehicleGroup = useLayerGroup(map)
    // routeId => marker on the last known location of the messenger
    const vehicleMarkers = useRef(new Map<string, L.Marker>())
    // routeId => position the messenger app passed on since the routes were loaded
    const messengerLocations = useRef<Record<string, LatLng>>({})

    const drawVehicleMarkers = () => {
        vehicleGroup?.clearLayers()
        vehicleMarkers.current.clear()

        const {reseller, routes, date, activeRouteTab, showRoutesOnMap, focusedRouteId} = latest.current

        if (!vehicleGroup || !routes || !showRoutesOnMap || activeRouteTab !== 'open') {
            return
        }

        for (const route of Object.values(routes)) {
            if (route.date !== date || route.hidden || (focusedRouteId && route._id !== focusedRouteId) || !route.started || route.completed) {
                continue
            }

            const location = messengerLocations.current[route._id] || route.messengerLocation

            if (!location?.lat || !location?.lng) {
                continue
            }

            const vehicleType = reseller?.settings?.vehicleTypes?.find((vehicleType) => vehicleType._id === route.vehicleTypeId)
            const iconColor = Colors.isDarkBackground(route.color) ? Colors.white : Colors.textDark

            const marker = L.marker(location, {
                zIndexOffset: 1000,
                icon: L.divIcon({
                    className: '',
                    iconSize: [30, 30],
                    iconAnchor: [15, 15],
                    html: `<div style='display: flex; align-items: center; justify-content: center; box-sizing: border-box; width: 30px; height: 30px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4); background: ${escapeHtml(route.color)};'>
                        <i class='mdi mdi-${escapeHtml(vehicleType?.icon || 'navigation')}' style='font-size: 18px; line-height: 18px; color: ${iconColor};'></i>
                        </div>`
                })
            })

            marker.bindTooltip(`<span>${escapeHtml(route.name || '')}</span>`)
            marker.on('click', () => OrderActions.setSelectedRoute(route._id))

            vehicleGroup.addLayer(marker)
            vehicleMarkers.current.set(route._id, marker)
        }
    }

    const drawVehicleMarkersRef = useLatest(drawVehicleMarkers)

    useEffect(() => {
        drawVehicleMarkers()
    }, [vehicleGroup, reseller, routes, date, activeRouteTab, showRoutesOnMap, focusedRouteId])

    // The messenger app passes its position on every minute per started route. Only the vehicle marker is moved,
    // rebuilding the map for every position would make the planning noticeably slower.
    useEffect(() => {
        const onUpdateMessengerLocation = (routeId: string, location: LatLng) => {
            messengerLocations.current[routeId] = location

            const marker = vehicleMarkers.current.get(routeId)

            if (marker) {
                marker.setLatLng(location)
            } else if (latest.current.routes?.[routeId]) {
                drawVehicleMarkersRef.current()
            }
        }

        socket.on('updateMessengerLocation', onUpdateMessengerLocation)

        return () => {
            socket.off('updateMessengerLocation', onUpdateMessengerLocation)
        }
    }, [])

    const createMarker = (model: MarkerModel, selected: boolean) => {
        const layer = model.icon ?
            L.marker(model.position, {
                pane: panes.lowMarkers.name,
                icon: L.icon({iconUrl: model.icon, iconSize: [48, 48], iconAnchor: [24, 27]})
            }) :
            L.circleMarker(model.position, {...model.circle, color: getMarkerBorderColor(selected), weight: 1})

        layer.bindTooltip(model.tooltip)

        if (model.orderIds.length) {
            layer.on('click', () => {
                const {onChange, onClick, selectedOrders} = latest.current

                if (selectingRef.current) {
                    onChange(toggleMarkerSelection(selectedOrders, model.orderIds))
                } else {
                    onClick(model.orderIds)
                }
            })
        }

        return layer
    }

    const updateLayers = (model: PlanningMapModel, selected: ReadonlySet<string>) => {
        const seen = new Set<string>()

        for (const marker of model.markers) {
            seen.add(marker.key)
            const entry = markerLayers.current.get(marker.key)

            if (entry?.model.signature === marker.signature) {
                entry.model = marker
                continue
            }

            entry?.group.removeLayer(entry.layer)

            const isSelected = isMarkerSelected(marker, selected)
            const group = marker.icon ? depotGroup : markerGroup
            const layer = createMarker(marker, isSelected)
            group.addLayer(layer)
            markerLayers.current.set(marker.key, {model: marker, layer, group, selected: isSelected})
        }

        for (const [key, entry] of markerLayers.current) {
            if (!seen.has(key)) {
                entry.group.removeLayer(entry.layer)
                markerLayers.current.delete(key)
            }
        }

        seen.clear()

        for (const line of model.polylines) {
            seen.add(line.key)
            const entry = lineLayers.current.get(line.key)
            const {model: previous} = entry || {}

            if (previous?.source === line.source && previous.color === line.color && previous.weight === line.weight && previous.opacity === line.opacity) {
                entry.model = line
                continue
            }

            entry?.layer.remove()

            const layer = L.polyline(line.latLngs, {color: line.color, weight: line.weight, opacity: line.opacity, interactive: false, renderer: lineRenderer.current})
            lineGroup.addLayer(layer)
            lineLayers.current.set(line.key, {model: line, layer})
        }

        for (const [key, entry] of lineLayers.current) {
            if (!seen.has(key)) {
                entry.layer.remove()
                lineLayers.current.delete(key)
            }
        }
    }

    const restyleMarkers = (selected: ReadonlySet<string>) => {
        for (const entry of markerLayers.current.values()) {
            const isSelected = isMarkerSelected(entry.model, selected)

            if (isSelected !== entry.selected && entry.layer instanceof L.CircleMarker) {
                entry.layer.setStyle({color: getMarkerBorderColor(isSelected)})
                entry.selected = isSelected
            }
        }
    }

    const update = ({data, selection, center}: UpdateKinds) => {
        if (!map || !depotGroup || !markerGroup || !lineGroup) {
            return
        }

        const props = latest.current
        const selected = new Set(props.selectedOrders)

        if (data) {
            const {depots, fitPositions} = getDepots(props.reseller, props.user, props.selectedBranchIds)
            const model = buildPlanningMapModel({
                date: props.date,
                orders: props.orders,
                routes: props.routes,
                activeRouteTab: props.activeRouteTab,
                depots,
                options: props.options || {},
                showOrdersOnMap: props.showOrdersOnMap,
                showCompletedAddressesOnMap: props.showCompletedAddressesOnMap,
                showRoutesOnMap: props.showRoutesOnMap,
                showStopNumbersOnMap: props.showStopNumbersOnMap,
                focusedRouteId: props.focusedRouteId
            })

            modelRef.current = model
            updateLayers(model, selected)

            if (autoFitPending.current) {
                if (model.stopPositions.length) {
                    fitBoundsSafe(map, [...fitPositions, ...model.stopPositions], {padding: [50, 50], maxZoom: 15})
                    stopAutoFit()
                } else if (!props.loading) {
                    stopAutoFit()
                }
            }

            // Zoom to a route once when it gets the focus
            if (props.focusedRouteId !== lastFocusedRouteId.current) {
                lastFocusedRouteId.current = props.focusedRouteId

                if (props.focusedRouteId && model.focusedRouteLatLngs) {
                    fitBoundsSafe(map, model.focusedRouteLatLngs)
                }
            }
        }

        if (data || selection) {
            restyleMarkers(selected)
        }

        if (center && props.centerViewOnSelected && props.selectedOrders.length && modelRef.current) {
            fitBoundsSafe(map, getOrderPositions(modelRef.current.markers, props.selectedOrders[0]), {padding: [50, 50], maxZoom: 17})
        }
    }

    // One update per frame however many props change (socket updates come in bursts), with everything asked for
    const pending = useRef<UpdateKinds>({})
    const frame = useRef(0)
    const updateRef = useLatest(update)

    const schedule = (kinds: UpdateKinds) => {
        pending.current = {
            data: pending.current.data || kinds.data,
            selection: pending.current.selection || kinds.selection,
            center: pending.current.center || kinds.center
        }

        if (!frame.current) {
            frame.current = requestAnimationFrame(() => {
                frame.current = 0
                const kinds = pending.current
                pending.current = {}
                updateRef.current(kinds)
            })
        }
    }

    useEffect(() => () => cancelAnimationFrame(frame.current), [])

    // A new group (another marker group, a new map) starts empty: take the layers out of the groups that stay and
    // rebuild them all
    useEffect(() => {
        markerLayers.current.forEach((entry) => entry.group.removeLayer(entry.layer))
        lineLayers.current.forEach((entry) => entry.layer.remove())
        markerLayers.current.clear()
        lineLayers.current.clear()
    }, [map, depotGroup, markerGroup, lineGroup])

    useEffect(() => {
        schedule({data: true})
    }, [map, depotGroup, markerGroup, lineGroup, date, orders, routes, activeRouteTab, options, showOrdersOnMap, showCompletedAddressesOnMap, showRoutesOnMap, showStopNumbersOnMap, focusedRouteId, reseller, user, selectedBranchIds, loading])

    useEffect(() => {
        schedule({selection: true})
    }, [selectedOrders])

    useEffect(() => {
        if (centerViewOnSelected) {
            schedule({center: true})
        }
    }, [centerViewOnSelected, selectedOrders, map])

    return (
        <div style={mapContainerStyle}>
            <div ref={containerRef} style={mapElementStyle} />

            <MapControls map={map} onZoom={stopAutoFit}>
                <MapControlCard>
                    {/* Selected while a held key selects with its shape as well */}
                    <MapControlButton
                        icon='mdi mdi-select-drag'
                        label={`${t('selectArea')} (Shift)`}
                        active={selecting === 'box'}
                        onClick={() => setSelectionMode(selectionMode === 'box' ? null : 'box')}
                    />

                    <MapControlButton
                        icon='mdi mdi-lasso'
                        label={`${t('selectLasso')} (${lassoKey})`}
                        active={selecting === 'lasso'}
                        onClick={() => setSelectionMode(selectionMode === 'lasso' ? null : 'lasso')}
                    />

                    <MapControlButton
                        icon='mdi mdi-hand-back-right-outline'
                        label={t('moveMap')}
                        active={!selecting}
                        onClick={() => setSelectionMode(null)}
                    />

                    <Popover
                        onToggle={setOptionsOpen}
                        content={
                            <div style={{padding: 6, width: 'fit-content'}} onClick={(event) => event.stopPropagation()}>
                                <FormStack>
                                    {activeRouteTab === 'open' &&
                                        <>
                                            <Toggle
                                                label={t('unplannedAddresses')}
                                                checked={showOrdersOnMap}
                                                onChange={OrderActions.setShowOrdersOnMap}
                                            />
                                            <Toggle
                                                label={t('addressesInRoutes')}
                                                checked={showRoutesOnMap}
                                                onChange={OrderActions.setShowRoutesOnMap}
                                            />
                                            <Toggle
                                                label={t('completedAddresses')}
                                                checked={showCompletedAddressesOnMap}
                                                onChange={OrderActions.setShowCompletedAddresses}
                                            />
                                        </>
                                    }

                                    <Toggle
                                        label={t('expectedAddresses')}
                                        checked={options.showExpectedOnMap}
                                        onChange={() => OrderActions.setMapOptions({showExpectedOnMap: !options.showExpectedOnMap})}
                                    />

                                    <Toggle
                                        label={t('stopNumbers')}
                                        checked={showStopNumbersOnMap}
                                        onChange={OrderActions.setShowStopNumbersOnMap}
                                    />

                                    <Toggle
                                        label={t('depots')}
                                        checked={options.showDepotsOnMap}
                                        onChange={() => OrderActions.setMapOptions({showDepotsOnMap: !options.showDepotsOnMap})}
                                    />
                                </FormStack>
                            </div>
                        }
                    >
                        {/* No tooltip while the options are open: it would cover the first one */}
                        <MapControlButton icon='mdi mdi-layers-outline' label={t('displayOptions')} hideTooltip={optionsOpen} />
                    </Popover>
                </MapControlCard>
            </MapControls>

            <MapLoadingOverlay loading={loading} />

            {((someRouteHidden && !focusedRouteId) || focusedRouteId) &&
                // Centred at the top; only the buttons take clicks, the rest of the row lets them through to the map
                <Row style={{position: 'absolute', left: 0, right: 0, top: mapSpacing.inset, zIndex: mapZIndex.controls, justifyContent: 'center', pointerEvents: 'none'}}>
                    <div style={{pointerEvents: 'auto'}}>
                        {focusedRouteId ?
                            <Button label={t('resetFocus')} onClick={() => OrderActions.setRouteFocus('')} /> :
                            <Button label={t('showAllHiddenRoutes')} onClick={() => OrderActions.resetRoutesHidden()} />
                        }
                    </div>
                </Row>
            }

            <MapAttribution withScale />
        </div>
    )
}

function getDepots(reseller: Reseller, user: User, selectedBranchIds?: string[]) {
    return getPlanningDepots({
        availableBranches: getAvailableBranches(reseller, user),
        selectedBranchIds,
        companyAddress: reseller?.settings?.companyAddress,
        sublocations: reseller?.settings?.sublocations
    })
}
